'use client';

import React from 'react';
import { Box, Typography } from '@mui/material';
import { color, spacing, borderRadius, breakpoints } from '@/theme/tokens';
import styled from '@emotion/styled';
import { TimelineItem } from '@/components/home/Timeline/types';
import { GRID_CONFIG } from '@/components/home/Timeline';
import {
    getCheckerboardColor,
    getCheckerboardColorWithCentering,
    getGridColumn,
    isLastInRow,
} from '@/components/home/Timeline/utils';
interface TimelineCardComponentProps {
    item: TimelineItem;
    index: number;
    totalItems: number;
}

const TimelineIcon = '/homepage/TimelineIcon.svg';

const TimelineCardWrapper = styled(Box)`
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;
`;

const TimelineCard = styled(Box)`
    width: 100%;
    display: flex;
    flex-direction: column;
    border-radius: ${borderRadius[16]} 0 ${borderRadius[16]} 0;
    overflow: hidden;
    height: 160px;

    @media (max-width: ${breakpoints.sm}) {
        max-width: 100%;
    }
`;

interface CardContentProps {
    xscolor: string;
    smcolor: string;
    mdcolor: string;
}

const CardContent = styled(Box)<CardContentProps>`
    padding: ${spacing[16]};
    display: flex;
    align-items: center;
    height: 100%;

    // xs: 2 columns
    background-color: ${({ xscolor }) => xscolor};

    // sm: 3 columns
    @media (min-width: ${breakpoints.sm}) {
        padding: ${spacing[20]};
        background-color: ${({ smcolor }) => smcolor};
    }

    // md: 5 columns
    @media (min-width: ${breakpoints.md}) {
        padding: ${spacing[24]};
        background-color: ${({ mdcolor }) => mdcolor};
    }
`;

const CardYear = styled(Box)`
    height: 47px;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    background-color: ${color.brand[50]};
    padding: ${spacing[12]} ${spacing[16]};

    @media (min-width: ${breakpoints.sm}) {
        padding: ${spacing[16]} ${spacing[20]};
    }

    @media (min-width: ${breakpoints.md}) {
        padding: ${spacing[12]} ${spacing[24]};
    }
`;

export const TimelineCardComponent = ({ item, index, totalItems }: TimelineCardComponentProps) => {
    const isLastItem = index === totalItems - 1;

    // Determine arrow visibility
    const hideArrowSm = isLastInRow(index, GRID_CONFIG.sm) || isLastItem;
    const hideArrowMd = isLastInRow(index, GRID_CONFIG.md) || isLastItem;

    // Calculate background colors for each breakpoint
    const xscolor = getCheckerboardColor(index, GRID_CONFIG.xs);
    // desctop and tablet use checkerboard with centering support
    const smcolor = getCheckerboardColorWithCentering(index, totalItems, GRID_CONFIG.sm);
    const mdcolor = getCheckerboardColorWithCentering(index, totalItems, GRID_CONFIG.md);

    // Calculate grid-column for centering items in last row
    const xsGridColumn = getGridColumn(index, totalItems, GRID_CONFIG.xs);
    const smGridColumn = getGridColumn(index, totalItems, GRID_CONFIG.sm);
    const mdGridColumn = getGridColumn(index, totalItems, GRID_CONFIG.md);

    return (
        <TimelineCardWrapper
            sx={{
                // xs: default styles
                gridColumn: xsGridColumn,

                // sm: 3 columns
                [`@media (min-width: ${breakpoints.sm})`]: {
                    gridColumn: smGridColumn,
                },

                // md: 5 columns
                [`@media (min-width: ${breakpoints.md})`]: {
                    gridColumn: mdGridColumn,
                },
            }}
        >
            <TimelineCard>
                <CardContent
                    xscolor={xscolor}
                    smcolor={smcolor}
                    mdcolor={mdcolor}
                >
                    <Typography
                        variant="label5semibold"
                        color={color.white[0]}
                        component="p"
                        sx={{
                            whiteSpace: {
                                xs: 'break-spaces',
                                sm: 'break-spaces',
                            },
                        }}
                    >
                        {item.title}
                    </Typography>
                </CardContent>
                <CardYear>
                    <Typography variant="heading1bold" color={color.white[0]} component="p">
                        {item.year}
                    </Typography>
                </CardYear>
            </TimelineCard>

            {/* Arrow icon positioned between cards */}
            <Box
                sx={{
                    position: 'absolute',
                    left: '100%',
                    top: '50%',
                    transform: 'translateX(calc((var(--grid-gap) - 24px) / 2)) translateY(-50%)',
                    // xs: always hidden
                    display: 'none',
                    // sm: show/hide based on row position
                    [`@media (min-width: ${breakpoints.sm})`]: {
                        display: hideArrowSm ? 'none' : 'block',
                    },
                    // md: show/hide based on row position
                    [`@media (min-width: ${breakpoints.md})`]: {
                        display: hideArrowMd ? 'none' : 'block',
                    },
                }}
            >
                <Box component="img" src={TimelineIcon} alt="Timeline Icon" />
            </Box>
        </TimelineCardWrapper>
    );
};
